import type { SkillDefinition } from "../../../schemas"

// Skill registry for Edition 0, Revision 1.
// A nonExhaustSurcharge of 0 means the modifier is not available.
// All activatable skills support Overload. Cheap skills surcharge it; larger skills discount it.

export const SKILL_REGISTRY: readonly SkillDefinition[] = [
  // Combat and Positioning
  {
    id: "guard",
    affinity: "yellow",
    isColorless: false,
    isPassive: true,
    supportsAllyRequirement: false,
    description:
      "While you control a Guard, enemy attacks must target a Guard unless they bypass Guard.",
    baseCostDelta: 2,
    nonExhaustSurcharge: 0,
    selfDestructSurcharge: 0,
  },
  {
    id: "swift",
    affinity: "red",
    isColorless: false,
    isPassive: true,
    supportsAllyRequirement: false,
    description:
      "This unit can attack the turn it enters the active slot or is swapped into it.",
    baseCostDelta: 1,
    nonExhaustSurcharge: 0,
    selfDestructSurcharge: 0,
  },
  {
    id: "sneak",
    affinity: "purple",
    isColorless: false,
    isPassive: true,
    supportsAllyRequirement: false,
    description:
      "When this unit attacks, it can ignore Guard and target Central Command or any unit.",
    baseCostDelta: 2,
    nonExhaustSurcharge: 0,
    selfDestructSurcharge: 0,
  },
  {
    id: "armor",
    affinity: "green",
    isColorless: false,
    isPassive: true,
    supportsAllyRequirement: false,
    description: "Reduce each hit to this unit by 1.",
    baseCostDelta: 1,
    nonExhaustSurcharge: 0,
    selfDestructSurcharge: 0,
  },
  {
    id: "breakthrough",
    affinity: "red",
    isColorless: false,
    isPassive: true,
    supportsAllyRequirement: false,
    description:
      "When this unit destroys a target in combat, any leftover attack can be used to attack again this turn.",
    baseCostDelta: 2,
    nonExhaustSurcharge: 0,
    selfDestructSurcharge: 0,
  },
  {
    id: "ranged",
    affinity: "colorless",
    isColorless: true,
    isPassive: true,
    supportsAllyRequirement: false,
    description: "This unit can attack from the support row.",
    baseCostDelta: 2,
    nonExhaustSurcharge: 0,
    selfDestructSurcharge: 0,
  },
  // Support and Utility
  {
    id: "repair",
    affinity: "green",
    isColorless: false,
    isPassive: false,
    supportsAllyRequirement: true,
    description:
      "Exhaust: this unit gets +2 health until after your opponent's next turn.",
    baseCostDelta: 2,
    nonExhaustSurcharge: 1.5,
    selfDestructSurcharge: -1,
  },
  {
    id: "rally",
    affinity: "yellow",
    isColorless: false,
    isPassive: false,
    supportsAllyRequirement: true,
    description: "Exhaust: this unit gets +1 attack until end of turn.",
    baseCostDelta: 1,
    nonExhaustSurcharge: 1.5,
    selfDestructSurcharge: 1,
  },
  {
    id: "jam",
    affinity: "blue",
    isColorless: false,
    isPassive: false,
    supportsAllyRequirement: true,
    description:
      "Exhaust: target enemy unit cannot attack or use exhausting skills on its next turn.",
    baseCostDelta: 2,
    nonExhaustSurcharge: 1.5,
    selfDestructSurcharge: -1,
  },
  {
    id: "shift",
    affinity: "purple",
    isColorless: false,
    isPassive: false,
    supportsAllyRequirement: true,
    description: "Exhaust: move this unit between the active and support rows.",
    baseCostDelta: 1,
    nonExhaustSurcharge: 1.5,
    selfDestructSurcharge: 1,
  },
  // Economy and Deckbuilding
  {
    id: "deploy",
    affinity: "red",
    isColorless: false,
    isPassive: true,
    supportsAllyRequirement: false,
    description:
      "When you play this unit, deal 1 damage to the enemy active unit.",
    baseCostDelta: 1,
    nonExhaustSurcharge: 0,
    selfDestructSurcharge: 0,
  },
  {
    id: "scrap",
    affinity: "colorless",
    isColorless: true,
    isPassive: false,
    supportsAllyRequirement: false,
    description: "Exhaust: scrap this unit and draw a card.",
    baseCostDelta: 0.5,
    nonExhaustSurcharge: 0,
    selfDestructSurcharge: 0,
    scrapsUnit: true,
  },
  {
    id: "recycle",
    affinity: "purple",
    isColorless: false,
    isPassive: false,
    supportsAllyRequirement: false,
    description:
      "Exhaust: discard one of your cards from your hand or board, then draw 2 cards.",
    baseCostDelta: 2,
    nonExhaustSurcharge: 0,
    selfDestructSurcharge: 0,
  },
  {
    id: "scout",
    affinity: "blue",
    isColorless: false,
    isPassive: false,
    supportsAllyRequirement: true,
    description: "Exhaust: draw a card.",
    baseCostDelta: 1,
    nonExhaustSurcharge: 1.5,
    selfDestructSurcharge: 1,
  },
  {
    id: "broker",
    affinity: "yellow",
    isColorless: false,
    isPassive: false,
    supportsAllyRequirement: true,
    description: "Exhaust: gain 1 energy this turn.",
    baseCostDelta: 2,
    nonExhaustSurcharge: 1.5,
    selfDestructSurcharge: -1,
  },
  {
    id: "salvage",
    affinity: "purple",
    isColorless: false,
    isPassive: false,
    supportsAllyRequirement: true,
    description:
      "Exhaust: return the top card of your discard pile to your hand.",
    baseCostDelta: 2,
    nonExhaustSurcharge: 1.5,
    selfDestructSurcharge: -1,
  },
  {
    id: "charge",
    affinity: "colorless",
    isColorless: true,
    isPassive: true,
    supportsAllyRequirement: false,
    description:
      "While this unit is in support, it generates 1 energy at the start of your turn.",
    baseCostDelta: 2,
    nonExhaustSurcharge: 0,
    selfDestructSurcharge: 0,
  },
  {
    id: "generator",
    affinity: "blue",
    isColorless: false,
    isPassive: true,
    supportsAllyRequirement: false,
    description:
      "While this unit is in support, each of your Charge units in support generates +1 energy at the start of your turn.",
    baseCostDelta: 2,
    nonExhaustSurcharge: 0,
    selfDestructSurcharge: 0,
  },
  {
    id: "spark",
    affinity: "colorless",
    isColorless: true,
    isPassive: true,
    supportsAllyRequirement: false,
    description: "When you play this unit, gain 1 energy.",
    baseCostDelta: 0.5,
    nonExhaustSurcharge: 0,
    selfDestructSurcharge: 0,
  },
  {
    id: "battery",
    affinity: "colorless",
    isColorless: true,
    isPassive: false,
    supportsAllyRequirement: false,
    description:
      "Exhaust: choose an amount up to your current energy; spend that energy and store it on this unit. At the start of your next turn, gain that amount +1. If this unit leaves play before then, the stored energy is lost.",
    baseCostDelta: 2,
    nonExhaustSurcharge: 0,
    selfDestructSurcharge: 0,
  },
  // Progression and Synergy
  {
    id: "ko",
    affinity: "red",
    isColorless: false,
    isPassive: true,
    supportsAllyRequirement: false,
    description: "When this unit destroys an enemy, draw a card.",
    baseCostDelta: 1,
    nonExhaustSurcharge: 0,
    selfDestructSurcharge: 0,
  },
  {
    id: "focus",
    affinity: "colorless",
    isColorless: true,
    isPassive: true,
    supportsAllyRequirement: false,
    description: "When this unit attacks, gain 1 energy.",
    baseCostDelta: 0.5,
    nonExhaustSurcharge: 0,
    selfDestructSurcharge: 0,
  },
  {
    id: "coordinated_strike",
    affinity: "yellow",
    isColorless: false,
    isPassive: true,
    supportsAllyRequirement: false,
    description:
      "While this unit is in support, your active unit gets +1 attack.",
    baseCostDelta: 1,
    nonExhaustSurcharge: 0,
    selfDestructSurcharge: 0,
  },
  {
    id: "recover",
    affinity: "green",
    isColorless: false,
    isPassive: false,
    supportsAllyRequirement: true,
    description: "Exhaust: ready your active unit.",
    baseCostDelta: 2,
    nonExhaustSurcharge: 1.5,
    selfDestructSurcharge: -1,
  },
  {
    id: "shield",
    affinity: "green",
    isColorless: false,
    isPassive: false,
    supportsAllyRequirement: true,
    description:
      "Exhaust: your active unit gets +1 health until after your opponent's next turn.",
    baseCostDelta: 1,
    nonExhaustSurcharge: 1.5,
    selfDestructSurcharge: 1,
  },
] as const

export const SKILL_MAP = new Map(SKILL_REGISTRY.map((s) => [s.id, s]))
